// File Metadata:
// Filename: QualityPhotos.js
// Version: 1.00
// Created: 2025-03-23 02:00:08
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState } from 'react'
import { 
    Box, Paper, Stack,
    Button, TextField, 
    IconButton,
    Typography,
    LinearProgress,
    Dialog,
    DialogContent,
    Card,
    CardMedia,
    CardContent,
    Grid,
    CardActionArea,
    ImageList,
    ImageListItem,
    ImageListItemBar
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadForOffline from '@mui/icons-material/DownloadForOffline'
import { Camera as CameraIcon } from '@mui/icons-material'
import { nanoid } from '@reduxjs/toolkit'
import uploadToImageKit from '../../../functions/imagekit'
import { useAddQualityPhotoMutation, useGetReceptionsQuery } from '../../api/receptionsApiSlice'
import Camera from '../../../components/Camera'
import useAuth from '../../../hooks/useAuth'
import { downloadAllImages } from '../../../functions/downloadUtils'

const QualityPhotos = ({ receptionId }) => {
    const { userId } = useAuth()
    const [addQualityPhoto] = useAddQualityPhotoMutation()
    const [photos, setPhotos] = useState([])
    const [showCamera, setShowCamera] = useState(false)
    const [progress, setProgress] = useState({})
    const [selectedPhoto, setSelectedPhoto] = useState(null)
    const [cameraOpen, setCameraOpen] = useState(false)
    
    const { reception } = useGetReceptionsQuery('receptionsList', {
        selectFromResult: ({ data }) => ({
            reception: data?.entities[receptionId]
        })
    })

    const handlePhotoCapture = (dataUri) => {
        const item = {
            img: dataUri,
            name: nanoid() + '.jpg',
            tags: [`reception_${receptionId}`],
            size: Math.ceil(dataUri.length * 0.75),
            uploadProgress: 0
        }
        setPhotos(prev => [...prev, item])
        setCameraOpen(false)
    }

    const handleNoteChange = (index, note) => {
        const updatedPhotos = [...photos]
        updatedPhotos[index].note = note
        setPhotos(updatedPhotos)
    }

    const handleDelete = (index) => {
        setPhotos(photos.filter((_, i) => i !== index))
    }

    const savePhoto = async (photo, index) => {
        try {
            const uploadData = {
                img: photo.img,
                name: photo.name,
                tags: photo.tags
            }

            const uploadedImage = await uploadToImageKit(uploadData, (percent) => {
                setProgress(prev => ({ ...prev, [photo.name]: percent }))
            })

            const photoData = {
                note: photo.note || 'No description provided',
                imageUrl: uploadedImage.url,
                fileId: uploadedImage.fileId,
                addedBy: userId
            }

            await addQualityPhoto({
                id: receptionId,
                photo: photoData
            }).unwrap()
            
            handleDelete(index)
        } catch (err) {
            console.error('Failed to save photo:', err?.data?.message || err.message)
        }
    }

    const handlePhotoClick = (photo) => {
        setSelectedPhoto(photo)
    }

    const handleCloseModal = () => {
        setSelectedPhoto(null)
    }

    const handleDownloadAll = async () => {
        if (reception?.qualityPhotos?.length) {
            await downloadAllImages(reception.qualityPhotos);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<CameraIcon />}
                    onClick={() => setCameraOpen(true)}
                >
                    Take Photo
                </Button>
                {reception?.qualityPhotos?.length > 0 && (
                    <Button
                        variant="outlined"
                        onClick={handleDownloadAll}
                        startIcon={<DownloadForOffline />}
                    >
                        Download All Photos
                    </Button>
                )}
            </Stack>

            {/* Camera Modal */}
            <Dialog
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <Camera 
                        onTakePhoto={handlePhotoCapture}
                        isImageMirror={false}
                        idealFacingMode="environment"
                    />
                </DialogContent>
            </Dialog>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Existing Photos
            </Typography>
            
            <Grid container spacing={2}>
                {reception?.qualityPhotos?.map((photo) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={photo._id}>
                        <Card>
                            <CardActionArea onClick={() => handlePhotoClick(photo)}>
                                <CardMedia
                                    component="img"
                                    height="200"
                                    image={`${photo.imageUrl}?tr=w-400,h-400`}
                                    alt="Quality photo"
                                    sx={{ objectFit: 'cover' }}
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {photo.note}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {new Date(photo.timestamp).toLocaleDateString()}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog 
                open={Boolean(selectedPhoto)} 
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0 }}>
                    {selectedPhoto && (
                        <Box sx={{ position: 'relative' }}>
                            <img
                                src={selectedPhoto.imageUrl}
                                alt="Quality photo"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '80vh',
                                    objectFit: 'contain'
                                }}
                            />
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body1">
                                    {selectedPhoto.note}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Added {new Date(selectedPhoto.timestamp).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ImageList sx={{ width: '100%' }} cols={1}>
                {photos.map((photo, index) => (
                    <Paper key={photo.name} sx={{ mb: 2, p: 2 }}>
                        <ImageListItem>
                            <img
                                src={photo.img}
                                alt={`Quality photo ${index + 1}`}
                                loading="lazy"
                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                            />
                            <ImageListItemBar
                                position="below"
                                actionIcon={
                                    <IconButton
                                        onClick={() => handleDelete(index)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            />
                        </ImageListItem>
                        <LinearProgress
                            variant="determinate"
                            color="success"
                            value={progress[photo.name] || 0}
                            sx={{ visibility: progress[photo.name] > 0 ? 'visible' : 'hidden' }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Add note about this photo..."
                            value={photo.note}
                            onChange={(e) => handleNoteChange(index, e.target.value)}
                            sx={{ mt: 1, mb: 1 }}
                        />
                        <Button
                            variant="contained"
                            disabled={!photo.note}
                            onClick={() => savePhoto(photo, index)}
                        >
                            Save Photo & Note
                        </Button>
                    </Paper>
                ))}
            </ImageList>
        </Box>
    )
}

export default QualityPhotos
